/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import styled from "styled-components";
import {
  TaxationLayout,
  TaxationRuleCreateModel,
  TaxationRuleModel,
  TaxationRuleUpdateModel,
} from "../../../../../domain/models/service-taxation-code";
import { NationalServiceModel } from "../../../../../domain/models/national-service";
import { ComplementaryLawModel } from "../../../../../data/usecases/complementary-law/remote-complementary-law";
import { theme } from "../../../../styles/theme";
import { formatNationalTaxCode } from "../../../../../utils/tax";

type Props = {
  visible: boolean;
  layout: TaxationLayout;
  editingRule: TaxationRuleModel | null;
  onClose: () => void;
  onSave: (data: TaxationRuleCreateModel | TaxationRuleUpdateModel) => void;
  nationalServices: NationalServiceModel[];
  complementaryLaws: ComplementaryLawModel[];
  pickerLoading: boolean;
  onSearchService: (query: string, layout: TaxationLayout) => void;
};

const RuleModal: React.FC<Props> = ({
  visible,
  layout,
  editingRule,
  onClose,
  onSave,
  nationalServices,
  complementaryLaws,
  pickerLoading,
  onSearchService,
}) => {
  const isEditing = !!editingRule;

  // Form state
  const [serviceId, setServiceId] = useState<number | null>(editingRule?.serviceId ?? null);
  const [codigo, setCodigo] = useState(editingRule?.codigo || "");
  const [codigoTributacao, setCodigoTributacao] = useState(editingRule?.codigoTributacao || "");
  const [sendCode, setSendCode] = useState(editingRule?.sendCode ?? true);
  const [serviceSearch, setServiceSearch] = useState("");

  const isMunicipal = layout === "MUNICIPAL";

  const serviceOptions = useMemo(() => {
    if (layout === "NACIONAL") {
      return nationalServices.map((ns) => ({
        value: ns.id,
        label: `${formatNationalTaxCode(ns.nationalTaxCode)} — ${ns.description}`,
      }));
    }
    return complementaryLaws.map((lc) => ({
      value: lc.id,
      label: `${lc.item} — ${lc.description}`,
    }));
  }, [layout, nationalServices, complementaryLaws]);

  const handleServiceSearch = useCallback(
    (query: string) => {
      setServiceSearch(query);
      onSearchService(query, layout);
    },
    [onSearchService, layout]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (isEditing) {
        const data: TaxationRuleUpdateModel = {
          codigo: isMunicipal ? codigo || null : null,
          codigoTributacao: codigoTributacao || null,
          sendCode,
        };
        onSave(data);
      } else {
        const data: TaxationRuleCreateModel = {
          layout,
          nationalServiceId: layout === "NACIONAL" ? serviceId : null,
          complementaryLawId: layout === "MUNICIPAL" ? serviceId : null,
          codigo: isMunicipal ? codigo || null : null,
          codigoTributacao: codigoTributacao || null,
          sendCode,
        };
        onSave(data);
      }
    },
    [isEditing, isMunicipal, layout, serviceId, codigo, codigoTributacao, sendCode, onSave]
  );

  return (
    <StyledDialog
      visible={visible}
      onHide={onClose}
      header={isEditing ? "Editar Regra" : "Nova Regra"}
      style={{ width: "500px" }}
      modal
      closable
    >
      <Form onSubmit={handleSubmit}>
        {/* Service picker (only when creating) */}
        {!isEditing && (
          <FormGroup>
            <Label>Serviço</Label>
            <SearchInput
              type="text"
              placeholder="Buscar serviço..."
              value={serviceSearch}
              onChange={(e) => handleServiceSearch(e.target.value)}
            />
            <SelectList>
              <SelectOption
                $selected={serviceId === null}
                onClick={() => setServiceId(null)}
              >
                Todos os serviços (coringa)
              </SelectOption>
              {pickerLoading && <LoadingText>Carregando...</LoadingText>}
              {serviceOptions.map((opt) => (
                <SelectOption
                  key={opt.value}
                  $selected={serviceId === opt.value}
                  onClick={() => setServiceId(opt.value)}
                >
                  {opt.label}
                </SelectOption>
              ))}
            </SelectList>
          </FormGroup>
        )}

        {isEditing && (
          <FormGroup>
            <Label>Serviço</Label>
            <ReadOnlyValue>
              {editingRule?.serviceCode
                ? `${editingRule.serviceCode} — ${editingRule.serviceLabel}`
                : editingRule?.serviceLabel || "Todos os serviços"}
            </ReadOnlyValue>
          </FormGroup>
        )}

        {/* Código (only municipal) */}
        {isMunicipal && (
          <FormGroup>
            <Label>Código</Label>
            <Input
              type="text"
              placeholder="Ex: 101"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
            />
          </FormGroup>
        )}

        {/* Código Tributação */}
        <FormGroup>
          <Label>Código de Tributação</Label>
          <Input
            type="text"
            placeholder="Ex: 10100"
            value={codigoTributacao}
            onChange={(e) => setCodigoTributacao(e.target.value)}
          />
        </FormGroup>

        {/* Send Code toggle */}
        <FormGroup>
          <CheckboxRow>
            <input
              type="checkbox"
              id="sendCode"
              checked={sendCode}
              onChange={(e) => setSendCode(e.target.checked)}
            />
            <label htmlFor="sendCode">Enviar campo (desmarcar = omitir a tag na emissão)</label>
          </CheckboxRow>
        </FormGroup>

        {/* Actions */}
        <ButtonRow>
          <CancelButton type="button" onClick={onClose}>
            Cancelar
          </CancelButton>
          <SubmitButton type="submit">
            {isEditing ? "Salvar" : "Criar"}
          </SubmitButton>
        </ButtonRow>
      </Form>
    </StyledDialog>
  );
};

export default RuleModal;

// --- Styled ---

const StyledDialog = styled(Dialog)`
  .p-dialog-header {
    background: ${theme.colors.darkBlue};
    color: ${theme.colors.white};
    font-family: ${theme.font.poppins};
  }

  .p-dialog-content {
    background: ${theme.colors.darkBlue};
    padding: 20px 24px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.white};
  font-family: ${theme.font.poppins};
`;

const Input = styled.input`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.gray800};
  background: ${theme.colors.blue900};
  color: ${theme.colors.white};
  font-size: 14px;
  font-family: ${theme.font.poppins};
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
  }
`;

const SearchInput = styled(Input)`
  margin-bottom: 4px;
`;

const SelectList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid ${theme.colors.gray800};
  border-radius: 8px;
  background: ${theme.colors.blue900};
`;

const SelectOption = styled.div<{ $selected: boolean }>`
  padding: 8px 12px;
  font-size: 13px;
  font-family: ${theme.font.poppins};
  color: ${(p) => (p.$selected ? theme.colors.white : "#c1c1c1")};
  background: ${(p) => (p.$selected ? theme.colors.primary : "transparent")};
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$selected ? theme.colors.primary : "rgba(255,255,255,0.05)")};
  }
`;

const LoadingText = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${theme.colors.gray800};
  font-style: italic;
`;

const ReadOnlyValue = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  background: ${theme.colors.blue900};
  color: ${theme.colors.white};
  font-size: 14px;
  font-family: ${theme.font.poppins};
  border: 1px solid ${theme.colors.gray800};
  opacity: 0.7;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 13px;
    color: ${theme.colors.white};
    font-family: ${theme.font.poppins};
    cursor: pointer;
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 100px;
  border: 1px solid ${theme.colors.gray800};
  background: transparent;
  color: ${theme.colors.gray800};
  font-family: ${theme.font.poppins};
  font-size: 13px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  border-radius: 100px;
  border: none;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  font-family: ${theme.font.poppins};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;
